import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import useAssistantConversation from "./useAssistantConversation";

const labels = {
  schedule: "Horarios",
  services: "Servicios",
  ai_config: "Configuración de Gina",
};
function Value({ section, value }) {
  const days = {
    monday: "Lunes",
    tuesday: "Martes",
    wednesday: "Miércoles",
    thursday: "Jueves",
    friday: "Viernes",
    saturday: "Sábado",
    sunday: "Domingo",
  };
  if (section === "schedule" && Array.isArray(value))
    return (
      <ul>
        {value.map((d) => (
          <li key={d.day}>
            {days[d.day]}:{" "}
            {d.is_open
              ? `${d.open_time}–${d.close_time}${d.break_start ? ` · descanso ${d.break_start}–${d.break_end}` : ""}`
              : "Cerrado"}
          </li>
        ))}
      </ul>
    );
  if (section === "services" && Array.isArray(value))
    return (
      <ul>
        {value.map((s) => (
          <li key={s.id}>
            {s.name} · {s.duration_minutes} min ·{" "}
            {s.price == null ? "Sin precio" : `$${s.price}`}{" "}
            {s.is_active === false ? "· Inactivo" : ""}
          </li>
        ))}
      </ul>
    );
  if (section === "ai_config" && value) {
    const fields = {
      greeting_message: "Saludo de bienvenida",
      personality: "Tono",
      language: "Idioma",
      can_schedule_appointments: "Agendar citas",
      can_provide_prices: "Informar precios",
      emergency_message: "Mensaje ante una urgencia",
      out_of_hours_message: "Mensaje fuera de horario",
    };
    const names = {
      professional: "Profesional",
      friendly: "Cercano",
      formal: "Formal",
      es: "Español",
      en: "Inglés",
    };
    return (
      <dl>
        {Object.entries(fields)
          .filter(([key]) => value[key] != null)
          .map(([key, label]) => (
            <div key={key}>
              <dt>
                <strong>{label}</strong>
              </dt>
              <dd>
                {typeof value[key] === "boolean"
                  ? value[key]
                    ? "Activado"
                    : "Desactivado"
                  : names[value[key]] || value[key]}
              </dd>
            </div>
          ))}
      </dl>
    );
  }
  return <p>Sin configuración previa</p>;
}
export default function AssistantPanel(props) {
  return props.model ? <AssistantView {...props} />
    : <ConnectedAssistantPanel key={`${props.clinicId}:${!!props.platform}`} {...props} />;
}

function ConnectedAssistantPanel(props) {
  const model = useAssistantConversation(props);
  return <AssistantView {...props} model={model} />;
}

function AssistantView({ model, platform = false, compact = false }) {
  const { context, messages, input, setInput, busy, loaded, error, notice, send, act, ticket, reload, refreshContext } = model;
  const log = useRef(null);
  useEffect(() => {
    if (log.current) log.current.scrollTop = log.current.scrollHeight;
  }, [messages.length, busy]);
  return (
    <section className="bo-assistant" aria-label="Asistente de configuración">
      <div className="bo-section-head">
        {!compact && <div>
          <span className="bo-eyebrow">ASISTENTE AIMP</span>
          <h2>Configura tu clínica conversando</h2>
          <p>Resuelve dudas y revisa cambios antes de aplicarlos.</p>
        </div>}
        <span className="bo-pill">{context?.name || "Cargando clínica…"}</span>
        {compact && <button type="button" className="bo-link" onClick={refreshContext} disabled={busy}>Actualizar pasos</button>}
      </div>
      {context && (
        <div className="bo-setup">
          {Object.entries(context.onboarding || {}).map(([key, ready]) => (
            <Link
              key={key}
              title={ready ? "Configuración guardada; comprueba su funcionamiento" : "Pendiente de configurar"}
              to={
                key === "schedule" || key === "services"
                  ? "/dashboard/clinic"
                  : context.links[key] || "/onboarding"
              }
            >
              {ready ? "✓" : "○"}{" "}
              {
                {
                  schedule: "Horarios",
                  services: "Servicios",
                  calendar: "Calendario",
                  whatsapp: "WhatsApp",
                }[key]
              }
            </Link>
          ))}
        </div>
      )}
      {!loaded && !error && <p role="status">Recuperando tu conversación…</p>}
      {!loaded && error && <button type="button" onClick={reload}>Reintentar carga</button>}
      {loaded && !messages.length && (
        <div className="bo-suggestions">
          {[
            "Ayúdame a configurar mi clínica",
            "Revisa qué falta para empezar",
            "Quiero cambiar mi horario de atención",
            "¿Cómo conecto mi calendario?",
          ].map((t) => (
            <button key={t} onClick={() => setInput(t)}>
              {t}
            </button>
          ))}
        </div>
      )}
      <div ref={log} className="bo-messages" role="log" aria-label="Conversación con el asistente" aria-live="polite" aria-busy={busy}>
        {messages.map((m, i) => (
          <article key={i} className={`bo-message ${m.role}`}>
            <strong>{m.role === "user" ? "Tú" : "Asistente"}</strong>
            <p>{m.text}</p>
            {m.proposal && (
              <div className="bo-proposal">
                <h3>{labels[m.proposal.section]}</h3>
                <p>{m.proposal.reason}</p>
                <div className="bo-compare">
                  <div>
                    <h4>Antes</h4>
                    <Value
                      section={m.proposal.section}
                      value={m.proposal.before}
                    />
                  </div>
                  <div>
                    <h4>Propuesta</h4>
                    <Value
                      section={m.proposal.section}
                      value={m.proposal.after}
                    />
                  </div>
                </div>
                <p>{m.proposal.notice}</p>
                {m.proposal.status === "pending" && (
                  <button
                    className="bo-primary"
                    disabled={busy}
                    onClick={() => act(m.proposal, "apply")}
                  >
                    Aplicar estos cambios
                  </button>
                )}
                {m.proposal.status === "applied" && (
                  <button
                    disabled={busy}
                    onClick={() => act(m.proposal, "undo")}
                  >
                    Deshacer cambio
                  </button>
                )}
                {m.proposal.status === "undone" && (
                  <span>Deshecho y verificado</span>
                )}
                {m.proposal.status === "needs_review" && (
                  <span>Resultado pendiente de revisión. Contacta a soporte antes de repetir el cambio.</span>
                )}
                {m.proposal.status === "expired" && (
                  <span>
                    Propuesta expirada. Solicita una nueva para revisar la
                    configuración actual.
                  </span>
                )}
              </div>
            )}
          </article>
        ))}
        {busy && <p className="aimp-thinking" role="status">El asistente está trabajando. Puedes seguir navegando.</p>}
      </div>
      {error && (
        <p className="bo-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="bo-notice" role="status">
          {notice}
        </p>
      )}
      <form onSubmit={send} className="bo-composer">
        <label htmlFor="assistant-question">¿Qué necesitas resolver?</label>
        <textarea
          id="assistant-question"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          maxLength={4000}
          placeholder="Por ejemplo: atendemos de lunes a viernes de 9 a 17 horas…"
        />
        <div className="bo-actions">
          {!platform && (
            <button type="button" disabled={busy || !loaded} onClick={ticket}>
              Abrir caso de soporte
            </button>
          )}
          <button className="bo-primary" disabled={busy || !loaded || !input.trim()}>
            {busy ? "Procesando…" : "Enviar consulta"}
          </button>
        </div>
      </form>
    </section>
  );
}

